import { useEffect, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { AdminApi } from "../../api/AdminApi";
import CommonDateRangePicker from "../common/CommonDateRangePicker";
import { dateValueToYYYYMMDD } from "../../helper/TimeHelper";
import {
    ALL_ORGANIZATION_ID,
    ALL_PICKLEBALL_DRILL_CODES,
    getPickleballSkillLabel,
    PICKLEBALL_DRILL_SKILLS,
} from "./pickleballDrillSkills";

const ALL_ORGANIZATION = {
    id: ALL_ORGANIZATION_ID,
    name_zh: "全部",
    name_en: "All",
};

const getOrgLabel = (org) => {
    if (!org) {
        return "";
    }
    if (org.id === ALL_ORGANIZATION_ID) {
        return "全部";
    }
    return `${org.name_en || ""} - ${org.name_zh || ""}`;
};

export default function PickleballDrillQueryForm({ onQuery, onExport }) {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [organizationList, setOrganizationList] = useState([]);
    const [selectedOrgs, setSelectedOrgs] = useState([ALL_ORGANIZATION]);
    const [skillCodes, setSkillCodes] = useState(ALL_PICKLEBALL_DRILL_CODES);

    useEffect(() => {
        AdminApi.getOrgnizationListCallback((isSuccess, msg, data) => {
            if (isSuccess === false) {
                toast.error(msg);
                setOrganizationList([]);
                return;
            }
            setOrganizationList(Array.isArray(data) ? data : []);
        });
    }, []);

    const onOrganizationsChanged = (event, newValue) => {
        const hadAll = selectedOrgs.some((org) => org.id === ALL_ORGANIZATION_ID);
        const hasAll = newValue.some((org) => org.id === ALL_ORGANIZATION_ID);

        if (newValue.length === 0) {
            setSelectedOrgs([ALL_ORGANIZATION]);
            return;
        }

        if (hasAll && !hadAll) {
            setSelectedOrgs([ALL_ORGANIZATION]);
            return;
        }

        if (hasAll && hadAll) {
            setSelectedOrgs(newValue.filter((org) => org.id !== ALL_ORGANIZATION_ID));
            return;
        }

        setSelectedOrgs(newValue);
    };

    const createQueryInput = () => {
        return {
            startDate: dateValueToYYYYMMDD(startDate),
            endDate: dateValueToYYYYMMDD(endDate),
            organizations: selectedOrgs,
            skillCodes,
        };
    };

    const onQueryClicked = () => {
        if (onQuery) {
            onQuery(createQueryInput());
        }
    };

    const onExportClicked = () => {
        if (onExport) {
            onExport(createQueryInput());
        }
    };

    return (
        <Card>
            <Box sx={{ padding: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <CommonDateRangePicker
                            onChange={([nextStartDate, nextEndDate]) => {
                                setStartDate(nextStartDate);
                                setEndDate(nextEndDate);
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={[ALL_ORGANIZATION, ...organizationList]}
                            value={selectedOrgs}
                            onChange={onOrganizationsChanged}
                            getOptionLabel={getOrgLabel}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField {...params} label="機構" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel id="pickleball-skill-type-label">技能類型</InputLabel>
                            <Select
                                labelId="pickleball-skill-type-label"
                                id="pickleball-skill-type"
                                multiple
                                value={skillCodes}
                                label="技能類型"
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSkillCodes(typeof value === "string" ? value.split(",") : value);
                                }}
                                renderValue={(selected) =>
                                    selected.map((code) => getPickleballSkillLabel(code)).join(", ")
                                }
                            >
                                {PICKLEBALL_DRILL_SKILLS.map((skill) => (
                                    <MenuItem key={skill.code} value={skill.code}>
                                        {skill.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid container spacing={2} pt={1}>
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Button variant="contained" onClick={onExportClicked}>匯出</Button>
                        <Button variant="contained" onClick={onQueryClicked}>查詢</Button>
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );
}
